import React, {
    createContext, memo, useEffect, useState, ReactNode, useContext,
    useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { SELECETED_PRODUCT_TYPE } from 'pages/ClientOverview/constants';
import { useThreeBProductType } from 'hooks/isThreeBProduct';

// --- Interfaces ---

export interface CommonDataEntity {
    Id: string | number;
    Name: string;
    [key: string]: any;
}

export interface CommonAllocationContextType {
    assetClasses: CommonDataEntity[] | undefined;
    securityTypes: CommonDataEntity[] | undefined;
    currencies: CommonDataEntity[] | undefined;
    countries: CommonDataEntity[] | undefined;
    sectors: CommonDataEntity[] | undefined;
    selectedProductType: string | null;
    setSelectedProductType: (type: string | null) => void;
    setupWithdrawalPlan: boolean;
    setSetupWithdrawalPlan: (value: boolean) => void;
}

// --- Initial State ---

const initialContext: CommonAllocationContextType = {
    assetClasses: undefined,
    securityTypes: undefined,
    currencies: undefined,
    countries: undefined,
    sectors: undefined,
    selectedProductType: null,
    setSelectedProductType: () => {},
    setupWithdrawalPlan: false,
    setSetupWithdrawalPlan: () => {},
};

export const CommonAllocationContext = createContext<CommonAllocationContextType>(initialContext);

// --- Provider Component ---

interface Props {
    children: ReactNode;
}

const CACHE_TIME = 30 * 60 * 1000;

const CommonAllocationDataProvider: React.FC<Props> = ({ children }) => {
    const { i18n } = useTranslation();
    const [selectedProductType, setSelectedProductType] = useState<string | null>(null);
    const [setupWithdrawalPlan, setSetupWithdrawalPlan] = useState<boolean>(false);
    const { isAzp } = useThreeBProductType({
        product: { externalId: sessionStorage.getItem(SELECETED_PRODUCT_TYPE) },
    });

    useEffect(() => {
        if (isAzp) {
            setSetupWithdrawalPlan(true);
        }
    }, [isAzp]);

    const { data: assetClassesData } = useGetCustomService({
        service: 'commonService', api: 'getAssetClasses', payload: i18n.language, enabled: true, gcTime: CACHE_TIME, staleTime: CACHE_TIME,
    });
    const { data: securityTypesData } = useGetCustomService({
        service: 'commonService', api: 'getSecurityTypes', payload: i18n.language, enabled: true, gcTime: CACHE_TIME, staleTime: CACHE_TIME,
    });
    const { data: currenciesData } = useGetCustomService({
        service: 'currenciesService', api: 'getCurrencies', payload: i18n.language, enabled: true, gcTime: CACHE_TIME, staleTime: CACHE_TIME,
    });
    const { data: countriesData } = useGetCustomService({
        service: 'commonService', api: 'getCountries', payload: i18n.language, enabled: true, gcTime: CACHE_TIME, staleTime: CACHE_TIME,
    });
    const { data: sectorsData } = useGetCustomService({
        service: 'commonService', api: 'getSectors', payload: i18n.language, enabled: true, gcTime: CACHE_TIME, staleTime: CACHE_TIME,
    });

    const assetClasses = assetClassesData?.data;
    const securityTypes = securityTypesData?.data;
    const currencies = currenciesData?.data?.Results || [];
    const countries = countriesData?.data;
    const sectors = sectorsData?.data;

    const contextValue: CommonAllocationContextType = useMemo(() => ({
        assetClasses,
        securityTypes,
        currencies,
        countries,
        sectors,
        selectedProductType,
        setSelectedProductType,
        setupWithdrawalPlan,
        setSetupWithdrawalPlan,
    }), [assetClasses, securityTypes, currencies, countries, sectors, selectedProductType, setupWithdrawalPlan]);

    return (
        <CommonAllocationContext.Provider value={contextValue}>
            {children}
        </CommonAllocationContext.Provider>
    );
};

export const CommonAllocationProvider = memo(CommonAllocationDataProvider);

/**
 * Custom hook to consume the CommonAllocation context safely
 */
export const useCommonAllocation = () => {
    const context = useContext(CommonAllocationContext);

    if (!context) {
        throw new Error('useCommonAllocation must be used within a CommonAllocationProvider');
    }

    return context;
};
